import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import Head from 'next/head';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { useIsAppView } from '@hooks/useIsAppView';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import WideLayout from '@components/layout/wide';
import {
  ROLE_LEVELS,
  hasSufficientPrivilegesWithNewUserRestrictions,
  isPaidMember
} from '@lib/utils';
import FixtureFilter from '@components/comparison/FixtureFilter';
import { fetchPlayerStats } from '@api-service/player-stats';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import type {
  PlayerStatsAggregation,
  PlayerStatsRow,
  PlayerStatsVenue
} from '@api-service/player-stats';
import PlayerChip from '@components/player/chip';
import PlayerSearch from '@components/player-search';
import PlayerStatsTable, {
  PlayerHeadToHeadStatsTable
} from '@components/comparison/stats-table';
import type { PlayerStatType } from '@components/comparison/stats-table';
import Select from '@components/atoms/Select';
import Title from '@components/title';
import {
  BodyRegular,
  CaptionBold,
  H1,
  H3,
  H4,
  SubtextRegular
} from '@components/atoms/typography';
import {
  GAMETIME_OPTIONS,
  PlayerImageChip,
  buildUpcomingGameweeks,
  initialState,
  resolveSeasonPlayerIds,
  toPredictionChartPlayer,
  togglePlayerOnSide
} from '@lib/page-utils/PlayerComparison';
import type {
  ComparisonFilters,
  ComparisonState,
  GametimeOption,
  UpcomingGameweek
} from '@lib/page-utils/PlayerComparison';
import { fetchPlayers } from '@api-service/players';
import type { Player as FfhPlayer } from '@api-service/players';
import Icon from '@components/atoms/icon';
import SuggestedTransferChart from '@components/suggested-transfer-chart';
import SpiderChart from '@components/spider-chart';
import {
  COMPARISON_SPIDER_FIELDS,
  buildComparisonSpiderSeries,
  buildMissingSeasonDataCaption,
  buildSpiderScaleCaption,
  findPlayersWithoutRows,
  getComparisonSpiderAxes
} from '@lib/page-utils/PlayerComparison/spider';
import { usePositionStatRanges } from '@hooks/usePositionStatRanges';
import { theme } from 'tailwind.theme';
import Tooltip from '@components/atoms/tooltip';
import { getPredictedPointsLevelStyles } from '@components/fixtures/utils/fixtureTypes';
import { getPageSeoHead, PAGE_SEO } from '@lib/page-seo';

const Paywall = dynamic(() => import('@components/paywall'));

const HEAD_TO_HEAD_STAT_DEFINITIONS: Array<{ label: string; path: string }> = [
  { label: 'Points', path: 'fpl.points' },
  { label: 'Goals', path: 'shooting.goals.total' },
  { label: 'xG', path: 'shooting.expectedGoals' },
  { label: 'xPts', path: 'fpl.expectedPoints' },
  { label: 'xA', path: 'creativity.expectedAssists' },
  { label: 'Assists', path: 'creativity.assists' }
];

const GAMETIME_AGGREGATION_MAP: Record<
  GametimeOption['value'],
  PlayerStatsAggregation
> = {
  perstart: 'perStart',
  perapp: 'perAppearance',
  per90: 'per90'
};

/**
 * The profile page links here with `?a=<optaId>&b=<optaId>` (comma-separated
 * for multiple players per side) so a comparison opens pre-populated.
 */
const parseOptaIdListParam = (value: string | string[] | undefined): string[] =>
  typeof value === 'string'
    ? value.split(',').filter((optaId) => optaId !== '')
    : [];

const readNumber = (row: PlayerStatsRow | undefined, path: string): number => {
  if (!row) return 0;
  let cursor: unknown = row;
  for (const segment of path.split('.')) {
    if (cursor && typeof cursor === 'object' && segment in cursor) {
      cursor = (cursor as Record<string, unknown>)[segment];
    } else {
      return 0;
    }
  }
  return typeof cursor === 'number' ? cursor : 0;
};

const sumStat = (
  rows: Array<PlayerStatsRow | undefined>,
  path: string
): number => rows.reduce((sum, row) => sum + readNumber(row, path), 0);

const buildSideStats = (
  uuids: string[],
  totalsByUuid: Map<string, PlayerStatsRow>,
  avgByUuid: Map<string, PlayerStatsRow>
): PlayerStatType[] => {
  const totals = uuids.map((id) => totalsByUuid.get(id));
  const avgs = uuids.map((id) => avgByUuid.get(id));
  return HEAD_TO_HEAD_STAT_DEFINITIONS.map(({ label, path }) => ({
    label,
    avg: sumStat(avgs, path),
    total: sumStat(totals, path).toString(),
    percent: 0
  }));
};

const applyPercentages = (
  statsA: PlayerStatType[],
  statsB: PlayerStatType[]
) => {
  for (let i = 0; i < statsA.length; i += 1) {
    const valueA = statsA[i]?.avg ?? 0;
    const valueB = statsB[i]?.avg ?? 0;
    const max = Math.max(valueA, valueB);
    if (statsA[i]) statsA[i].percent = max > 0 ? valueA / max : 0;
    if (statsB[i]) statsB[i].percent = max > 0 ? valueB / max : 0;
  }
};

const ComparisonPreview = ({ isPulsing }: { isPulsing: boolean }) => (
  <div
    className={isPulsing ? 'animate-pulse' : 'opacity-40'}
    aria-hidden="true"
  >
    <div>
      <Image
        className="w-full"
        src="/images/skeleton/player-comparison/predicted-points.png"
        height={350}
        width={1000}
        priority
        alt="Predicted points placeholder"
        objectFit="contain"
      />
    </div>
    <div>
      <Image
        className="w-full"
        src="/images/skeleton/player-comparison/head-to-head.png"
        height={600}
        width={1000}
        priority
        alt="Head-to-head placeholder"
        objectFit="contain"
      />
    </div>
  </div>
);

const Comparison = () => {
  const router = useRouter();
  const { isAppView } = useIsAppView();
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken } = useAccessToken();
  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;
  const canUseComparisonTool = useMemo(
    () => hasSufficientPrivilegesWithNewUserRestrictions(user, ROLE_LEVELS.PRO),
    [user]
  );
  const canViewPredictedPoints = isPaidMember(user);
  const toolSeason = context?.season ?? null;
  const pageHead = getPageSeoHead(router.pathname, PAGE_SEO.playerComparison);
  const isPlayerComparisonPageClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.playerComparisonPageIsClosed,
    false
  );

  const [state, setState] = useState<ComparisonState>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSeasonStats, setHasSeasonStats] = useState(true);
  const [hasSeededFromQuery, setHasSeededFromQuery] = useState(false);

  useEffect(() => {
    if (toolSeason) {
      setState((prev) => ({
        ...prev,
        filters: { ...prev.filters, season: toolSeason }
      }));
    }
  }, [toolSeason]);

  const seedOptaIdsAParam =
    typeof router.query.a === 'string' ? router.query.a : null;
  const seedOptaIdsBParam =
    typeof router.query.b === 'string' ? router.query.b : null;

  // Links from the player profile land here with players named in the URL;
  // fetch their rows once and pre-fill the two sides. Predictions cannot be
  // requested anonymously and the tool is paywalled anyway, so the seed waits
  // for an authorised user and their access token.
  useEffect(() => {
    if (hasSeededFromQuery || !router.isReady) return undefined;

    const seedOptaIdsA = parseOptaIdListParam(seedOptaIdsAParam ?? undefined);
    const seedOptaIdsB = parseOptaIdListParam(seedOptaIdsBParam ?? undefined);
    if (seedOptaIdsA.length === 0 && seedOptaIdsB.length === 0) {
      setHasSeededFromQuery(true);
      return undefined;
    }

    if (isUserLoading) return undefined;
    if (!canUseComparisonTool) {
      setHasSeededFromQuery(true);
      return undefined;
    }
    if (!accessToken) return undefined;

    let isCancelled = false;

    void (async () => {
      try {
        const uniqueOptaIds = [...new Set([...seedOptaIdsA, ...seedOptaIdsB])];
        const response = await fetchPlayers({
          optaIds: uniqueOptaIds.join(','),
          limit: uniqueOptaIds.length,
          accessToken
        });
        if (isCancelled) return;

        const playerByOptaId = new Map(
          response.data.map((player) => [player.optaId, player])
        );
        const toPlayers = (optaIds: string[]) =>
          optaIds
            .map((optaId) => playerByOptaId.get(optaId))
            .filter((player): player is FfhPlayer => player !== undefined);

        setState((prev) => ({
          ...prev,
          playersA:
            prev.playersA.length > 0 ? prev.playersA : toPlayers(seedOptaIdsA),
          playersB:
            prev.playersB.length > 0 ? prev.playersB : toPlayers(seedOptaIdsB)
        }));
      } catch {
        // A failed seed just leaves the pickers empty, as if the page had
        // been opened without the query.
      } finally {
        if (!isCancelled) setHasSeededFromQuery(true);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    hasSeededFromQuery,
    router.isReady,
    seedOptaIdsAParam,
    seedOptaIdsBParam,
    isUserLoading,
    canUseComparisonTool,
    accessToken
  ]);

  const uuidsA = state.playersA.map((player) => player.id);
  const uuidsB = state.playersB.map((player) => player.id);
  const optaIdsA = state.playersA.map((player) => player.optaId);
  const optaIdsB = state.playersB.map((player) => player.optaId);
  const uuidsAKey = uuidsA.join(',');
  const uuidsBKey = uuidsB.join(',');
  const aggregationToken = state.gametimeType.value;
  const { minGW, maxGW, venue, season } = state.filters;

  useEffect(() => {
    if (uuidsA.length === 0 && uuidsB.length === 0) {
      setHasSeasonStats(true);
      setState((prev) =>
        prev.playerStatsA.length === 0 &&
        prev.playerStatsB.length === 0 &&
        prev.statsRowsA.length === 0 &&
        prev.statsRowsB.length === 0
          ? prev
          : {
              ...prev,
              playerStatsA: [],
              playerStatsB: [],
              statsRowsA: [],
              statsRowsB: []
            }
      );
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    // FFH-2373: a comparison run is a settled selection or filter change that
    // actually computes stats. Firing here (not on the add/remove clicks)
    // also counts URL-seeded comparisons from ?a=/?b= share links.
    captureAnalyticsEvent(AnalyticsEvents.playerComparisonRun, {
      player_count: uuidsA.length + uuidsB.length
    });

    const aggregation = GAMETIME_AGGREGATION_MAP[aggregationToken];

    void (async () => {
      try {
        // Player ids are re-minted each season, so a past-season lookup must
        // resolve that season's ids from the stable optaIds first; the stats
        // endpoint silently returns no rows for ids from another season.
        let statsIdsA = uuidsA;
        let statsIdsB = uuidsB;
        const isPastSeason = toolSeason !== null && season !== toolSeason;
        if (isPastSeason) {
          const seasonIdByOptaId = await resolveSeasonPlayerIds(
            [...optaIdsA, ...optaIdsB],
            season,
            accessToken ?? undefined
          );
          const toSeasonIds = (optaIds: string[]) =>
            optaIds
              .map((optaId) => seasonIdByOptaId.get(optaId))
              .filter((id): id is string => Boolean(id));
          statsIdsA = toSeasonIds(optaIdsA);
          statsIdsB = toSeasonIds(optaIdsB);
        }

        const allStatsIds = [...statsIdsA, ...statsIdsB];
        let totalsRows: PlayerStatsRow[] = [];
        let avgRows: PlayerStatsRow[] = [];
        if (allStatsIds.length > 0) {
          const sharedParams = {
            ids: allStatsIds.join(','),
            fields: COMPARISON_SPIDER_FIELDS,
            ...(accessToken ? { accessToken } : {}),
            minGameweek: minGW,
            maxGameweek: maxGW,
            venue: venue as PlayerStatsVenue,
            season,
            limit: allStatsIds.length
          };
          const [totalsResponse, avgResponse] = await Promise.all([
            fetchPlayerStats({ ...sharedParams, aggregation: 'total' }),
            fetchPlayerStats({ ...sharedParams, aggregation })
          ]);
          totalsRows = totalsResponse.data;
          avgRows = avgResponse.data;
        }

        const totalsByUuid = new Map(totalsRows.map((row) => [row.id, row]));
        const avgByUuid = new Map(avgRows.map((row) => [row.id, row]));

        const statsA = buildSideStats(statsIdsA, totalsByUuid, avgByUuid);
        const statsB = buildSideStats(statsIdsB, totalsByUuid, avgByUuid);
        applyPercentages(statsA, statsB);

        if (!cancelled) {
          setHasSeasonStats(totalsRows.length > 0 || avgRows.length > 0);
          setState((prev) => ({
            ...prev,
            playerStatsA: statsA,
            playerStatsB: statsB,
            statsRowsA: statsIdsA
              .map((id) => totalsByUuid.get(id))
              .filter((row): row is PlayerStatsRow => row !== undefined),
            statsRowsB: statsIdsB
              .map((id) => totalsByUuid.get(id))
              .filter((row): row is PlayerStatsRow => row !== undefined)
          }));
        }
      } catch {
        if (!cancelled) {
          setHasSeasonStats(false);
          setState((prev) => ({
            ...prev,
            playerStatsA: [],
            playerStatsB: [],
            statsRowsA: [],
            statsRowsB: []
          }));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    uuidsAKey,
    uuidsBKey,
    aggregationToken,
    minGW,
    maxGW,
    venue,
    season,
    toolSeason,
    accessToken
  ]);

  const selectedPlayers = useMemo(
    () => [...state.playersA, ...state.playersB],
    [state.playersA, state.playersB]
  );
  const selectedPositions = useMemo(
    () => [...new Set(selectedPlayers.map((player) => player.position))],
    [selectedPlayers]
  );
  // A side can come back empty for a past season when its player was not in
  // the league then. The chart still draws the side that has data and the
  // caption says who is missing, rather than the whole panel vanishing.
  const hasSpiderSelection =
    state.statsRowsA.length > 0 || state.statsRowsB.length > 0;

  const spiderAxes = useMemo(
    () => getComparisonSpiderAxes(selectedPositions, season),
    [selectedPositions, season]
  );
  const spiderStatPaths = useMemo(
    () => spiderAxes.map((axis) => axis.statPath),
    [spiderAxes]
  );

  const { getRange } = usePositionStatRanges({
    positions: selectedPositions,
    fields: COMPARISON_SPIDER_FIELDS,
    statPaths: spiderStatPaths,
    aggregation: 'total',
    venue: state.filters.venue as PlayerStatsVenue,
    minGameweek: state.filters.minGW,
    maxGameweek: state.filters.maxGW,
    season: state.filters.season,
    ...(accessToken ? { accessToken } : {}),
    enabled: hasSpiderSelection
  });

  const spiderSeries = useMemo(() => {
    if (!hasSpiderSelection) return [];
    const sides = [
      {
        key: 'A',
        label: state.playersA.map((player) => player.displayName).join(', '),
        color: theme.colors.primary.DEFAULT,
        rows: state.statsRowsA,
        positions: state.playersA.map((player) => player.position)
      },
      {
        key: 'B',
        label: state.playersB.map((player) => player.displayName).join(', '),
        color: theme.colors.danger.DEFAULT,
        rows: state.statsRowsB,
        positions: state.playersB.map((player) => player.position)
      }
    ].filter((side) => side.rows.length > 0);
    return buildComparisonSpiderSeries(sides, getRange, spiderAxes);
  }, [
    hasSpiderSelection,
    getRange,
    spiderAxes,
    state.playersA,
    state.playersB,
    state.statsRowsA,
    state.statsRowsB
  ]);

  // Only a past-season lookup can drop a selected player, during the optaId
  // to season-id resolution, so the caption is limited to that case.
  const missingSeasonDataCaption = useMemo(() => {
    if (toolSeason === null || season === toolSeason) return null;
    return buildMissingSeasonDataCaption(
      findPlayersWithoutRows(selectedPlayers, [
        ...state.statsRowsA,
        ...state.statsRowsB
      ]),
      season
    );
  }, [selectedPlayers, state.statsRowsA, state.statsRowsB, season, toolSeason]);

  const spiderScaleSentence = buildSpiderScaleCaption(selectedPositions);
  const spiderScaleDescription = missingSeasonDataCaption
    ? `${missingSeasonDataCaption} ${spiderScaleSentence}`
    : spiderScaleSentence;

  const togglePlayer = (side: 'A' | 'B') => (player: FfhPlayer) =>
    setState((prev) => togglePlayerOnSide(prev, side, player));

  const setGametime = (option: GametimeOption) =>
    setState((prev) => ({ ...prev, gametimeType: option }));

  const updateFilters = (
    gameweekRange: number[],
    nextVenue: string,
    nextSeason: number
  ) =>
    setState((prev) => ({
      ...prev,
      filters: {
        minGW: gameweekRange[0],
        maxGW: gameweekRange[1],
        venue: nextVenue as ComparisonFilters['venue'],
        season: nextSeason
      }
    }));

  if (isPlayerComparisonPageClosed) {
    return (
      <>
        <Head>{pageHead}</Head>
        <WideLayout>
          <div className="bg-gray-50 px-2 pt-2 lg:pt-8 pb-10 sm:px-4">
            <EndOfSeasonHoldingMessage
              pageTitle="The Player Comparison tool"
              isAppView={isAppView}
            />
          </div>
        </WideLayout>
      </>
    );
  }

  const isAnyPlayerSelected =
    state.playersA.length > 0 || state.playersB.length > 0;
  const showHeadToHead = state.playersA.length > 0 && state.playersB.length > 0;
  // Predictions are zeroed (not omitted) when unavailable, e.g. before the
  // new season's model is live, so an all-zero set means "no data" rather
  // than a genuinely flat forecast.
  const hasChartPredictions = [...state.playersA, ...state.playersB].some(
    (player) =>
      player.fixtures.some((fixture) => fixture.predictions.points > 0)
  );
  // The chart always plots the next five gameweeks, so it only makes sense
  // when comparing the current season.
  const isCurrentSeasonSelected = toolSeason === null || season === toolSeason;

  const playerChips = (players: FfhPlayer[], side: 'A' | 'B', border: string) =>
    players.map((player) => (
      <PlayerChip
        key={player.optaId}
        player={player}
        handleDelete={togglePlayer(side)}
        border={border}
      />
    ));

  return (
    <div>
      <Head>{pageHead}</Head>
      <Title>Comparison Tool</Title>
      <div className="container max-w-[675px] mx-auto">
        <div className="relative px-2 pt-2 lg:pt-8 pb-20 sm:px-6 lg:pb-10">
          <H1 className="py-3 px-2 lg:px-0 lg:hidden">Player Comparison</H1>
          <div className="relative mx-auto max-w-7xl pb-14">
            {isUserLoading ? (
              <ComparisonPreview isPulsing />
            ) : !canUseComparisonTool ? (
              <div className="p-4 pt-8">
                <Paywall
                  variant="embedded"
                  title="🔒 Unlock the comparison tool"
                />
              </div>
            ) : (
              <>
                <div className="flex pt-3">
                  <div className="flex flex-col flex-1">
                    <PlayerSearch
                      searchId="player-search-a"
                      onPlayerClick={togglePlayer('A')}
                      hasPaywall={false}
                      selectedPlayers={state.playersA}
                      onDeletePlayerChip={togglePlayer('A')}
                      selectedPlayerBorder="border-t-primary"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <BodyRegular className="ml-1 mr-2">vs.</BodyRegular>
                  </div>
                  <div className="flex flex-col flex-1">
                    <PlayerSearch
                      searchId="player-search-b"
                      onPlayerClick={togglePlayer('B')}
                      hasPaywall={false}
                      selectedPlayers={state.playersB}
                      onDeletePlayerChip={togglePlayer('B')}
                      selectedPlayerBorder="border-t-danger-600"
                    />
                  </div>
                </div>

                {!isAnyPlayerSelected || isLoading ? (
                  <div className="relative">
                    <ComparisonPreview isPulsing={isLoading} />
                    {!isLoading && (
                      <div className="absolute inset-x-0 top-16 flex justify-center px-4">
                        <div className="max-w-md rounded-lg bg-white px-6 py-5 text-center shadow-500">
                          <H4 className="mb-1">Pick players to compare</H4>
                          <BodyRegular className="mb-0 text-neutral-600">
                            Add players to each side with the search boxes above
                            to see predicted points, upcoming fixtures and
                            head-to-head stats.
                          </BodyRegular>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                      <H3 className="mb-3">Filters</H3>
                      <div className="max-w-xs">
                        <Select
                          name="gametime"
                          onChange={setGametime}
                          label="Gametime"
                          options={GAMETIME_OPTIONS}
                          value={state.gametimeType}
                        />
                      </div>
                      {toolSeason && (
                        <div className="mt-4">
                          <FixtureFilter
                            initialState={state.filters}
                            onUpdate={updateFilters}
                          />
                        </div>
                      )}
                      <div className="flex justify-center space-x-3">
                        <ul className="mt-5">
                          {playerChips(state.playersA, 'A', 'border-t-primary')}
                        </ul>
                        <ul className="mt-5">
                          {playerChips(
                            state.playersB,
                            'B',
                            'border-t-danger-600'
                          )}
                        </ul>
                      </div>
                    </div>

                    {showHeadToHead && isCurrentSeasonSelected && (
                      <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                        <H3>Predicted points</H3>
                        {hasChartPredictions ? (
                          <div className="mt-3 h-[280px]">
                            <SuggestedTransferChart
                              variant="standard"
                              playerOutColor={theme.colors['danger'].DEFAULT}
                              playerInColor={theme.colors['primary'].DEFAULT}
                              playersIn={state.playersA.map(
                                toPredictionChartPlayer
                              )}
                              playersOut={state.playersB.map(
                                toPredictionChartPlayer
                              )}
                            />
                          </div>
                        ) : (
                          <BodyRegular className="mt-3 mb-0 text-neutral-600">
                            Predicted points for these players aren&apos;t
                            available yet. They&apos;ll appear here once our
                            predictions for the upcoming gameweeks are live.
                          </BodyRegular>
                        )}
                      </div>
                    )}

                    {state.playersA.length > 0 && (
                      <div className="shadow-100 p-5 bg-white rounded-lg my-5 space-y-3">
                        <H3 className="mb-3">Upcoming fixtures</H3>
                        {state.playersA.map((player) => (
                          <UpcomingFixtureRow
                            key={`fixture-A-${player.optaId}`}
                            player={player}
                            nextGameweek={nextGameweek}
                            borderClass="border-primary"
                            canViewPredictedPoints={canViewPredictedPoints}
                          />
                        ))}
                        {state.playersB.map((player) => (
                          <UpcomingFixtureRow
                            key={`fixture-B-${player.optaId}`}
                            player={player}
                            nextGameweek={nextGameweek}
                            borderClass="border-danger-600"
                            canViewPredictedPoints={canViewPredictedPoints}
                          />
                        ))}
                      </div>
                    )}

                    {!hasSeasonStats && (
                      <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                        <H3>Head-to-head</H3>
                        <BodyRegular className="mt-3 mb-0 text-neutral-600">
                          No match data for the selected season and filters yet.
                          If the season hasn&apos;t started, pick a previous
                          season in the filters above.
                        </BodyRegular>
                      </div>
                    )}

                    {showHeadToHead &&
                      hasSeasonStats &&
                      spiderSeries.length > 0 && (
                        <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                          <H3>Player profile</H3>
                          <SpiderChart
                            className="mt-3"
                            chartClassName="h-[320px] sm:h-[380px]"
                            axes={spiderAxes}
                            series={spiderSeries}
                            showScaleTicks={false}
                            scaleDescription={spiderScaleDescription}
                          />
                        </div>
                      )}

                    {showHeadToHead && hasSeasonStats && (
                      <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                        <H3>Head-to-head (Total)</H3>
                        <PlayerHeadToHeadStatsTable
                          playersA={state.playersA}
                          playersB={state.playersB}
                          playerStatsA={state.playerStatsA}
                          playerStatsB={state.playerStatsB}
                        />
                      </div>
                    )}

                    {state.playersA.length > 0 && hasSeasonStats && (
                      <div className="shadow-100 p-5 bg-white rounded-lg my-5">
                        <H3 className="mb-5">
                          Head-to-head ({state.gametimeType.label})
                        </H3>
                        <PlayerStatsTable
                          statsA={state.playerStatsA}
                          statsB={state.playerStatsB}
                          playersA={state.playersA}
                          playersB={state.playersB}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const UPCOMING_GAMEWEEK_COUNT = 6;

type UpcomingFixtureTileProps = {
  gameweek: UpcomingGameweek;
  canViewPredictedPoints: boolean;
};

const UpcomingFixtureTile = ({
  gameweek,
  canViewPredictedPoints
}: UpcomingFixtureTileProps) => {
  const hasFixtures = gameweek.fixtures.length > 0;
  const tileStyles =
    hasFixtures && canViewPredictedPoints
      ? getPredictedPointsLevelStyles(gameweek.predictedPoints)
      : 'text-neutral-800 bg-neutral-200';

  return (
    <div className={`cursor-default rounded-lg ${tileStyles}`}>
      <CaptionBold
        fontStyles="text-xs md:text-sm"
        className="px-1 text-center leading-[14px] pt-1"
      >
        {!hasFixtures && '-'}
        {hasFixtures &&
          (canViewPredictedPoints ? (
            gameweek.predictedPoints.toFixed(1)
          ) : (
            <Tooltip variant="dark" title="FFHub Members">
              <span
                role="img"
                aria-label="Predicted points are available to members only"
                className="text-gray-400"
              >
                <Icon name="Lock" fontSize="small" />
              </span>
            </Tooltip>
          ))}
      </CaptionBold>
      {hasFixtures ? (
        gameweek.fixtures.map((fixture, index) => (
          <SubtextRegular
            key={`${gameweek.gameweek}-${fixture.opponentShortName}-${index}`}
            fontStyles="text-xxxs md:text-xxs"
            className={`px-1 text-center leading-[12px] ${
              index === gameweek.fixtures.length - 1 ? 'pb-1' : ''
            }`}
          >
            {fixture.opponentShortName} ({fixture.isHome ? 'H' : 'A'})
          </SubtextRegular>
        ))
      ) : (
        <SubtextRegular
          fontStyles="text-xxxs md:text-xxs"
          className="px-1 text-center leading-[12px] pb-1"
        >
          Blank
        </SubtextRegular>
      )}
    </div>
  );
};

type UpcomingFixtureRowProps = {
  player: FfhPlayer;
  nextGameweek: number | null;
  borderClass: string;
  canViewPredictedPoints: boolean;
};

const UpcomingFixtureRow = ({
  player,
  nextGameweek,
  borderClass,
  canViewPredictedPoints
}: UpcomingFixtureRowProps) => {
  // `nextGameweek` is null until GameweekContextSync resolves; the gameweek
  // window is built from it, so skip the row until we have a real number
  // rather than flashing a wrong six.
  if (typeof nextGameweek !== 'number') return null;
  const gameweeks = buildUpcomingGameweeks(
    player,
    nextGameweek,
    UPCOMING_GAMEWEEK_COUNT
  );
  return (
    <div className={`flex border-l-4 pl-2 ${borderClass}`}>
      <div className="py-4 w-[140px] flex flex-col items-start space-y-3">
        <PlayerImageChip player={player} />
      </div>
      <div className="grid grid-cols-6 gap-0.5 sm:gap-1 items-center flex-1">
        {gameweeks.map((gameweek) => (
          <UpcomingFixtureTile
            key={gameweek.gameweek}
            gameweek={gameweek}
            canViewPredictedPoints={canViewPredictedPoints}
          />
        ))}
      </div>
    </div>
  );
};

export default Comparison;
